import { useCallback, useRef, useState } from 'react'
import { DropResult } from 'react-beautiful-dnd'

const insert = <T>(arr: T[], insertedIndex: number, newItem: T): T[] => {
  return [...arr.slice(0, insertedIndex), newItem, ...arr.slice(insertedIndex)]
}

const reorder = <T>(list: T[], startIndex: number, endIndex: number): T[] => {
  const result = Array.from(list)
  const [removed] = result.splice(startIndex, 1)
  result.splice(endIndex, 0, removed)
  return result
}

const remove = <T>(list: T[], removedIndex: number): T[] => {
  const result = Array.from(list)
  result.splice(removedIndex, 1)
  return result
}

export interface IUseDraggableListProps<T> {
  /**
   * @example [{ id: 1, name: 'foo' }, { id: 2, name: 'bar' }]
   */
  list: T[]
}

export const useDraggableList = <T>({ list: listProp = [] }: IUseDraggableListProps<T>) => {
  const initialList = useRef(listProp)
  const [list, setList] = useState<T[]>([...listProp])

  const reset = useCallback((newList: T[] = initialList.current) => {
    setList(newList)
  }, [])

  /**
   * @example reorderItem(0, 1)
   */
  const reorderItem = useCallback((startIndex: number, endIndex: number) => {
    setList(prevList => [...reorder(prevList, startIndex, endIndex)])
  }, [])

  /**
   * @example addItem({ id: 1, name: 'joe' }, 2)
   */
  const addItem = useCallback(
    (newItem: T, insertedIndex?: number) => {
      const index = insertedIndex ?? list.length
      setList(prevList => {
        return [...insert(prevList, index, newItem)]
      })
    },
    [list.length]
  )

  /**
   * @example removeItem(0)
   */
  const removeItem = useCallback((removedIndex: number) => {
    setList(prevList => [...remove(prevList, removedIndex)])
  }, [])

  /**
   * For use with <DraggableList /> and `onDragEnd` prop
   */
  const dragEndHandler = useCallback(
    (result: DropResult) => {
      const { destination, source } = result || {}
      const { index: sourceIndex } = source || {}
      const { index: destinationIndex = sourceIndex } = destination || {}
      reorderItem(sourceIndex, destinationIndex)
    },
    [reorderItem]
  )

  return {
    dragEndHandler,
    list,
    reorderItem,
    removeItem,
    addItem,
    reset,
  }
}
